import { localizeWithPath, localize2WithPath } from 'vscode/vscode/vs/nls';
import { GlobalCompareResourcesAction, FocusFilesExplorer, ShowActiveFileInExplorer, CompareWithClipboardAction, CompareNewUntitledTextFilesAction, ToggleAutoSaveAction, OpenActiveFileInEmptyWorkspace, SetActiveEditorReadonlyInSession, SetActiveEditorWriteableInSession, ToggleActiveEditorReadonlyInSession, ResetActiveEditorReadonlyInSession, renameHandler, moveFileToTrashHandler, deleteFileHandler, cutFileHandler, copyFileHandler, pasteFileHandler, openFilePreserveFocusHandler, NEW_FILE_COMMAND_ID, NEW_FILE_LABEL, NEW_FOLDER_COMMAND_ID, NEW_FOLDER_LABEL, COPY_FILE_LABEL, PASTE_FILE_LABEL, FileCopiedContext, DOWNLOAD_COMMAND_ID, DOWNLOAD_LABEL, UPLOAD_COMMAND_ID, UPLOAD_LABEL, TRIGGER_RENAME_LABEL, MOVE_FILE_TO_TRASH_LABEL } from 'vscode/vscode/vs/workbench/contrib/files/browser/fileActions';
import { acceptLocalChangesCommand, revertLocalChangesCommand, CONFLICT_RESOLUTION_CONTEXT } from 'vscode/vscode/vs/workbench/contrib/files/browser/editors/textFileSaveErrorHandler';
import { registerAction2, MenuRegistry, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { openWindowCommand, newWindowCommand } from './fileCommands.js';
import { COPY_PATH_COMMAND_ID, COPY_RELATIVE_PATH_COMMAND_ID, REVEAL_IN_EXPLORER_COMMAND_ID, SAVE_FILE_COMMAND_ID, SAVE_FILE_LABEL, SAVE_FILE_WITHOUT_FORMATTING_COMMAND_ID, SAVE_FILE_WITHOUT_FORMATTING_LABEL, SAVE_ALL_IN_GROUP_COMMAND_ID, SAVE_FILES_COMMAND_ID, REVERT_FILE_COMMAND_ID, COMPARE_WITH_SAVED_COMMAND_ID, SAVE_FILE_AS_COMMAND_ID, SAVE_FILE_AS_LABEL, NEW_UNTITLED_FILE_COMMAND_ID, NEW_UNTITLED_FILE_LABEL, OPEN_TO_SIDE_COMMAND_ID, OpenEditorsDirtyEditorContext, OpenEditorsGroupContext, OpenEditorsReadonlyEditorContext, COMPARE_RESOURCE_COMMAND_ID, ResourceSelectedForCompareContext, SELECT_FOR_COMPARE_COMMAND_ID, COMPARE_SELECTED_COMMAND_ID, OPEN_WITH_EXPLORER_COMMAND_ID, REMOVE_ROOT_FOLDER_COMMAND_ID, REMOVE_ROOT_FOLDER_LABEL, SAVE_ALL_COMMAND_ID } from 'vscode/vscode/vs/workbench/contrib/files/browser/fileConstants';
import { CommandsRegistry } from 'vscode/vscode/vs/platform/commands/common/commands';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { KeybindingsRegistry } from 'vscode/vscode/vs/platform/keybinding/common/keybindingsRegistry';
import { FilesExplorerFocusCondition, ExplorerRootContext, ExplorerResourceNotReadonlyContext, ExplorerResourceMoveableToTrash, ExplorerResourceCut, ExplorerFolderContext, ExplorerResourceAvailableEditorIdsContext, FoldersViewVisibleContext } from 'vscode/vscode/vs/workbench/contrib/files/common/files';
import { ADD_ROOT_FOLDER_COMMAND_ID, ADD_ROOT_FOLDER_LABEL } from 'vscode/vscode/vs/workbench/browser/actions/workspaceCommands';
import { REOPEN_WITH_COMMAND_ID, CLOSE_EDITOR_COMMAND_ID, CLOSE_OTHER_EDITORS_IN_GROUP_COMMAND_ID, CLOSE_SAVED_EDITORS_COMMAND_ID, CLOSE_EDITORS_IN_GROUP_COMMAND_ID } from 'vscode/vscode/vs/workbench/browser/parts/editor/editorCommands';
import { AutoSaveAfterShortDelayContext } from 'vscode/vscode/vs/workbench/services/filesConfiguration/common/filesConfigurationService';
import { WorkbenchListDoubleSelection } from 'vscode/vscode/vs/platform/list/browser/listService';
import { Schemas } from 'vscode/vscode/vs/base/common/network';
import { ResourceContextKey, WorkspaceFolderCountContext, ActiveEditorAvailableEditorIdsContext, DirtyWorkingCopiesContext, HasWebFileSystemAccess, EnterMultiRootWorkspaceSupportContext, WorkbenchStateContext, ActiveEditorContext, SidebarFocusContext, ActiveEditorCanRevertContext } from 'vscode/vscode/vs/workbench/common/contextkeys';
import { IsWebContext } from 'vscode/vscode/vs/platform/contextkey/common/contextkeys';
import { IExplorerService } from 'vscode/vscode/vs/workbench/contrib/files/browser/files.service';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';

const _moduleId = "vs/workbench/contrib/files/browser/fileActions.contribution";
registerAction2(GlobalCompareResourcesAction);
registerAction2(FocusFilesExplorer);
registerAction2(ShowActiveFileInExplorer);
registerAction2(CompareWithClipboardAction);
registerAction2(CompareNewUntitledTextFilesAction);
registerAction2(ToggleAutoSaveAction);
registerAction2(OpenActiveFileInEmptyWorkspace);
registerAction2(SetActiveEditorReadonlyInSession);
registerAction2(SetActiveEditorWriteableInSession);
registerAction2(ToggleActiveEditorReadonlyInSession);
registerAction2(ResetActiveEditorReadonlyInSession);
CommandsRegistry.registerCommand('_files.windowOpen', openWindowCommand);
CommandsRegistry.registerCommand('_files.newWindow', newWindowCommand);
const explorerCommandsWeightBonus = 10;
const RENAME_ID = 'renameFile';
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: RENAME_ID,
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(
        FilesExplorerFocusCondition,
         (ExplorerRootContext.toNegated()),
        ExplorerResourceNotReadonlyContext
    ))),
    primary: 60 ,
    mac: {
        primary: 3
    },
    handler: renameHandler
});
const MOVE_FILE_TO_TRASH_ID = 'moveFileToTrash';
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: MOVE_FILE_TO_TRASH_ID,
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(
        FilesExplorerFocusCondition,
        ExplorerResourceNotReadonlyContext,
        ExplorerResourceMoveableToTrash
    ))),
    primary: 20 ,
    mac: {
        primary: 2048  | 1 ,
        secondary: [20 ]
    },
    handler: moveFileToTrashHandler
});
const DELETE_FILE_ID = 'deleteFile';
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: DELETE_FILE_ID,
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(FilesExplorerFocusCondition, ExplorerResourceNotReadonlyContext))),
    primary: 1024  | 20 ,
    mac: {
        primary: 2048  | 512  | 1
    },
    handler: deleteFileHandler
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: DELETE_FILE_ID,
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(
        FilesExplorerFocusCondition,
        ExplorerResourceNotReadonlyContext,
         (ExplorerResourceMoveableToTrash.toNegated())
    ))),
    primary: 20 ,
    mac: {
        primary: 2048  | 1
    },
    handler: deleteFileHandler
});
const CUT_FILE_ID = 'filesExplorer.cut';
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: CUT_FILE_ID,
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(
        FilesExplorerFocusCondition,
         (ExplorerRootContext.toNegated()),
        ExplorerResourceNotReadonlyContext
    ))),
    primary: 2048  | 54 ,
    handler: cutFileHandler,
});
const COPY_FILE_ID = 'filesExplorer.copy';
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: COPY_FILE_ID,
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(
        FilesExplorerFocusCondition,
         (ExplorerRootContext.toNegated())
    ))),
    primary: 2048  | 33 ,
    handler: copyFileHandler,
});
const PASTE_FILE_ID = 'filesExplorer.paste';
CommandsRegistry.registerCommand(PASTE_FILE_ID, pasteFileHandler);
KeybindingsRegistry.registerKeybindingRule({
    id: `^${PASTE_FILE_ID}`,
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(FilesExplorerFocusCondition, ExplorerResourceNotReadonlyContext))),
    primary: 2048  | 52 ,
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: 'filesExplorer.cancelCut',
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(FilesExplorerFocusCondition, ExplorerResourceCut))),
    primary: 9 ,
    handler: async (accessor) => {
        const explorerService = accessor.get(IExplorerService);
        await explorerService.setToCopy([], true);
    }
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: 'filesExplorer.openFilePreserveFocus',
    weight: 200  + explorerCommandsWeightBonus,
    when: ( (ContextKeyExpr.and(
        FilesExplorerFocusCondition,
         (ExplorerFolderContext.toNegated())
    ))),
    primary: 10 ,
    handler: openFilePreserveFocusHandler
});
const copyPathCommand = {
    id: COPY_PATH_COMMAND_ID,
    title: ( localizeWithPath(_moduleId, 0, "Copy Path"))
};
const copyRelativePathCommand = {
    id: COPY_RELATIVE_PATH_COMMAND_ID,
    title: ( localizeWithPath(_moduleId, 1, "Copy Relative Path"))
};
appendEditorTitleContextMenuItem(COPY_PATH_COMMAND_ID, copyPathCommand.title, ResourceContextKey.IsFileSystemResource, '1_cutcopypaste');
appendEditorTitleContextMenuItem(COPY_RELATIVE_PATH_COMMAND_ID, copyRelativePathCommand.title, ResourceContextKey.IsFileSystemResource, '1_cutcopypaste');
appendEditorTitleContextMenuItem(REVEAL_IN_EXPLORER_COMMAND_ID, ( localizeWithPath(_moduleId, 2, "Reveal in Explorer View")), ResourceContextKey.IsFileSystemResource, '2_files', 1);
function appendEditorTitleContextMenuItem(id, title, when, group, order) {
    MenuRegistry.appendMenuItem(MenuId.EditorTitleContext, {
        command: { id, title },
        when,
        group,
        order
    });
}
appendSaveConflictEditorTitleAction('workbench.files.action.acceptLocalChanges', ( localizeWithPath(_moduleId, 3, "Use your changes and overwrite file contents")), Codicon.check, -10, acceptLocalChangesCommand);
appendSaveConflictEditorTitleAction('workbench.files.action.revertLocalChanges', ( localizeWithPath(_moduleId, 4, "Discard your changes and revert to file contents")), Codicon.discard, -9, revertLocalChangesCommand);
function appendSaveConflictEditorTitleAction(id, title, icon, order, command) {
    CommandsRegistry.registerCommand(id, command);
    MenuRegistry.appendMenuItem(MenuId.EditorTitle, {
        command: { id, title, icon },
        when: ( (ContextKeyExpr.equals(CONFLICT_RESOLUTION_CONTEXT, true))),
        group: 'navigation',
        order
    });
}
function appendToCommandPalette({ id, title, category, metadata }, when) {
    MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
        command: {
            id,
            title,
            category,
            metadata
        },
        when
    });
}
appendToCommandPalette({
    id: COPY_PATH_COMMAND_ID,
    title: ( localize2WithPath(_moduleId, 5, "Copy Path of Active File")),
    category: Categories.File
});
appendToCommandPalette({
    id: COPY_RELATIVE_PATH_COMMAND_ID,
    title: ( localize2WithPath(_moduleId, 6, "Copy Relative Path of Active File")),
    category: Categories.File
});
appendToCommandPalette({
    id: SAVE_FILE_COMMAND_ID,
    title: SAVE_FILE_LABEL,
    category: Categories.File
});
appendToCommandPalette({
    id: SAVE_FILE_WITHOUT_FORMATTING_COMMAND_ID,
    title: SAVE_FILE_WITHOUT_FORMATTING_LABEL,
    category: Categories.File
});
appendToCommandPalette({
    id: SAVE_ALL_IN_GROUP_COMMAND_ID,
    title: ( localize2WithPath(_moduleId, 7, "Save All in Group")),
    category: Categories.File
});
appendToCommandPalette({
    id: SAVE_FILES_COMMAND_ID,
    title: ( localize2WithPath(_moduleId, 8, "Save All Files")),
    category: Categories.File
});
appendToCommandPalette({
    id: REVERT_FILE_COMMAND_ID,
    title: ( localize2WithPath(_moduleId, 9, "Revert File")),
    category: Categories.File
});
appendToCommandPalette({
    id: COMPARE_WITH_SAVED_COMMAND_ID,
    title: ( localize2WithPath(_moduleId, 10, "Compare Active File with Saved")),
    category: Categories.File,
    metadata: {
        description: ( localize2WithPath(
            _moduleId,
            11,
            "Opens a new diff editor to compare the active file with the version on disk."
        ))
    }
});
appendToCommandPalette({
    id: SAVE_FILE_AS_COMMAND_ID,
    title: SAVE_FILE_AS_LABEL,
    category: Categories.File
});
appendToCommandPalette({
    id: NEW_FILE_COMMAND_ID,
    title: NEW_FILE_LABEL,
    category: Categories.File
}, ( (WorkspaceFolderCountContext.notEqualsTo('0'))));
appendToCommandPalette({
    id: NEW_FOLDER_COMMAND_ID,
    title: NEW_FOLDER_LABEL,
    category: Categories.File,
    metadata: { description: ( localize2WithPath(_moduleId, 12, "Create a new folder or directory")) }
}, ( (WorkspaceFolderCountContext.notEqualsTo('0'))));
appendToCommandPalette({
    id: NEW_UNTITLED_FILE_COMMAND_ID,
    title: NEW_UNTITLED_FILE_LABEL,
    category: Categories.File
});
const isFileOrUntitledResourceContextKey = ( (ContextKeyExpr.or(
    ResourceContextKey.IsFileSystemResource,
     (ResourceContextKey.Scheme.isEqualTo(Schemas.untitled))
)));
const openToSideCommand = {
    id: OPEN_TO_SIDE_COMMAND_ID,
    title: ( localizeWithPath(_moduleId, 13, "Open to the Side"))
};
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: 'navigation',
    order: 10,
    command: openToSideCommand,
    when: isFileOrUntitledResourceContextKey
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '1_open',
    order: 10,
    command: {
        id: REOPEN_WITH_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 14, "Reopen Editor With..."))
    },
    when: ActiveEditorAvailableEditorIdsContext
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '1_cutcopypaste',
    order: 10,
    command: copyPathCommand,
    when: ResourceContextKey.IsFileSystemResource
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '1_cutcopypaste',
    order: 20,
    command: copyRelativePathCommand,
    when: ResourceContextKey.IsFileSystemResource
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '2_save',
    order: 10,
    command: {
        id: SAVE_FILE_COMMAND_ID,
        title: SAVE_FILE_LABEL,
        precondition: OpenEditorsDirtyEditorContext
    },
    when: ( (ContextKeyExpr.or(
    (ResourceContextKey.Scheme.isEqualTo(Schemas.untitled)),
    (ContextKeyExpr.and(
    (OpenEditorsGroupContext.toNegated()),
    (OpenEditorsReadonlyEditorContext.toNegated()),
    (AutoSaveAfterShortDelayContext.toNegated()))))))
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '2_save',
    order: 20,
    command: {
        id: REVERT_FILE_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 9, "Revert File")),
        precondition: OpenEditorsDirtyEditorContext
    },
    when: ( (ContextKeyExpr.and(
    (OpenEditorsGroupContext.toNegated()),
    (OpenEditorsReadonlyEditorContext.toNegated()),
    (ResourceContextKey.Scheme.notEqualsTo(Schemas.untitled)),
    (AutoSaveAfterShortDelayContext.toNegated()))))
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '2_save',
    order: 30,
    command: {
        id: SAVE_ALL_IN_GROUP_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 15, "Save All")),
        precondition: DirtyWorkingCopiesContext
    },
    when: OpenEditorsGroupContext
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '3_compare',
    order: 10,
    command: {
        id: COMPARE_WITH_SAVED_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 16, "Compare with Saved")),
        precondition: OpenEditorsDirtyEditorContext
    },
    when: ( (ContextKeyExpr.and(
        ResourceContextKey.IsFileSystemResource,
         (AutoSaveAfterShortDelayContext.toNegated()),
         (WorkbenchListDoubleSelection.toNegated())
    )))
});
const compareResourceCommand = {
    id: COMPARE_RESOURCE_COMMAND_ID,
    title: ( localizeWithPath(_moduleId, 17, "Compare with Selected"))
};
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '3_compare',
    order: 20,
    command: compareResourceCommand,
    when: ( (ContextKeyExpr.and(
        ResourceContextKey.HasResource,
        ResourceSelectedForCompareContext,
        isFileOrUntitledResourceContextKey,
         (WorkbenchListDoubleSelection.toNegated())
    )))
});
const selectForCompareCommand = {
    id: SELECT_FOR_COMPARE_COMMAND_ID,
    title: ( localizeWithPath(_moduleId, 18, "Select for Compare"))
};
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '3_compare',
    order: 30,
    command: selectForCompareCommand,
    when: ( (ContextKeyExpr.and(
        ResourceContextKey.HasResource,
        isFileOrUntitledResourceContextKey,
         (WorkbenchListDoubleSelection.toNegated())
    )))
});
const compareSelectedCommand = {
    id: COMPARE_SELECTED_COMMAND_ID,
    title: ( localizeWithPath(_moduleId, 19, "Compare Selected"))
};
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '3_compare',
    order: 30,
    command: compareSelectedCommand,
    when: ( (ContextKeyExpr.and(
        ResourceContextKey.HasResource,
        WorkbenchListDoubleSelection,
        isFileOrUntitledResourceContextKey
    )))
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '4_close',
    order: 10,
    command: {
        id: CLOSE_EDITOR_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 20, "Close"))
    },
    when: ( (OpenEditorsGroupContext.toNegated()))
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '4_close',
    order: 20,
    command: {
        id: CLOSE_OTHER_EDITORS_IN_GROUP_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 21, "Close Others"))
    },
    when: ( (OpenEditorsGroupContext.toNegated()))
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '4_close',
    order: 30,
    command: {
        id: CLOSE_SAVED_EDITORS_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 22, "Close Saved"))
    }
});
MenuRegistry.appendMenuItem(MenuId.OpenEditorsContext, {
    group: '4_close',
    order: 40,
    command: {
        id: CLOSE_EDITORS_IN_GROUP_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 23, "Close All"))
    }
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: 'navigation',
    order: 4,
    command: {
        id: NEW_FILE_COMMAND_ID,
        title: NEW_FILE_LABEL,
        precondition: ExplorerResourceNotReadonlyContext
    },
    when: ExplorerFolderContext
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: 'navigation',
    order: 6,
    command: {
        id: NEW_FOLDER_COMMAND_ID,
        title: NEW_FOLDER_LABEL,
        precondition: ExplorerResourceNotReadonlyContext
    },
    when: ExplorerFolderContext
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: 'navigation',
    order: 10,
    command: openToSideCommand,
    when: ( (ContextKeyExpr.and(
         (ExplorerFolderContext.toNegated()),
        ResourceContextKey.HasResource
    )))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: 'navigation',
    order: 20,
    command: {
        id: OPEN_WITH_EXPLORER_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 24, "Open With...")),
    },
    when: ( (ContextKeyExpr.and(
         (ExplorerFolderContext.toNegated()),
        ExplorerResourceAvailableEditorIdsContext
    ))),
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '3_compare',
    order: 20,
    command: compareResourceCommand,
    when: ( (ContextKeyExpr.and(
         (ExplorerFolderContext.toNegated()),
        ResourceContextKey.HasResource,
        ResourceSelectedForCompareContext,
         (WorkbenchListDoubleSelection.toNegated())
    )))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '3_compare',
    order: 30,
    command: selectForCompareCommand,
    when: ( (ContextKeyExpr.and(
         (ExplorerFolderContext.toNegated()),
        ResourceContextKey.HasResource,
         (WorkbenchListDoubleSelection.toNegated())
    )))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '3_compare',
    order: 30,
    command: compareSelectedCommand,
    when: ( (ContextKeyExpr.and(
         (ExplorerFolderContext.toNegated()),
        ResourceContextKey.HasResource,
        WorkbenchListDoubleSelection
    )))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '5_cutcopypaste',
    order: 8,
    command: {
        id: CUT_FILE_ID,
        title: ( localizeWithPath(_moduleId, 25, "Cut"))
    },
    when: ( (ContextKeyExpr.and(
         (ExplorerRootContext.toNegated()),
        ExplorerResourceNotReadonlyContext
    )))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '5_cutcopypaste',
    order: 10,
    command: {
        id: COPY_FILE_ID,
        title: COPY_FILE_LABEL
    },
    when: ( (ExplorerRootContext.toNegated()))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '5_cutcopypaste',
    order: 20,
    command: {
        id: PASTE_FILE_ID,
        title: PASTE_FILE_LABEL,
        precondition: ( (ContextKeyExpr.and(ExplorerResourceNotReadonlyContext, FileCopiedContext)))
    },
    when: ExplorerFolderContext
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, ({
    group: '5b_importexport',
    order: 10,
    command: {
        id: DOWNLOAD_COMMAND_ID,
        title: DOWNLOAD_LABEL
    },
    when: ( (ContextKeyExpr.or(
    (ContextKeyExpr.and(
         (IsWebContext.toNegated()),
         (ResourceContextKey.Scheme.notEqualsTo(Schemas.file))
    )),
    (ContextKeyExpr.and(
        IsWebContext,
         (ExplorerFolderContext.toNegated()),
         (ExplorerRootContext.toNegated())
    )),
    (ContextKeyExpr.and(IsWebContext, HasWebFileSystemAccess)))))
}));
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, ({
    group: '5b_importexport',
    order: 20,
    command: {
        id: UPLOAD_COMMAND_ID,
        title: UPLOAD_LABEL,
    },
    when: ( (ContextKeyExpr.and(
    IsWebContext,
    ExplorerFolderContext,
    ExplorerResourceNotReadonlyContext)))
}));
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '6_copypath',
    order: 10,
    command: copyPathCommand,
    when: ResourceContextKey.IsFileSystemResource
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '6_copypath',
    order: 20,
    command: copyRelativePathCommand,
    when: ResourceContextKey.IsFileSystemResource
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '2_workspace',
    order: 10,
    command: {
        id: ADD_ROOT_FOLDER_COMMAND_ID,
        title: ADD_ROOT_FOLDER_LABEL
    },
    when: ( (ContextKeyExpr.and(ExplorerRootContext,  (ContextKeyExpr.or(
        EnterMultiRootWorkspaceSupportContext,
         (WorkbenchStateContext.isEqualTo('workspace'))
    )))))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '2_workspace',
    order: 30,
    command: {
        id: REMOVE_ROOT_FOLDER_COMMAND_ID,
        title: REMOVE_ROOT_FOLDER_LABEL
    },
    when: ( (ContextKeyExpr.and(
        ExplorerRootContext,
        ExplorerFolderContext,
         (ContextKeyExpr.and(
             (WorkspaceFolderCountContext.notEqualsTo('0')),
             (ContextKeyExpr.or(
                EnterMultiRootWorkspaceSupportContext,
                 (WorkbenchStateContext.isEqualTo('workspace'))
            ))
        ))
    )))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '7_modification',
    order: 10,
    command: {
        id: RENAME_ID,
        title: TRIGGER_RENAME_LABEL,
        precondition: ExplorerResourceNotReadonlyContext
    },
    when: ( (ExplorerRootContext.toNegated()))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '7_modification',
    order: 20,
    command: {
        id: MOVE_FILE_TO_TRASH_ID,
        title: MOVE_FILE_TO_TRASH_LABEL,
        precondition: ExplorerResourceNotReadonlyContext
    },
    alt: {
        id: DELETE_FILE_ID,
        title: ( localizeWithPath(_moduleId, 26, "Delete Permanently")),
        precondition: ExplorerResourceNotReadonlyContext
    },
    when: ( (ContextKeyExpr.and(
         (ExplorerRootContext.toNegated()),
        ExplorerResourceMoveableToTrash
    )))
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    group: '7_modification',
    order: 20,
    command: {
        id: DELETE_FILE_ID,
        title: ( localizeWithPath(_moduleId, 26, "Delete Permanently")),
        precondition: ExplorerResourceNotReadonlyContext
    },
    when: ( (ContextKeyExpr.and(
         (ExplorerRootContext.toNegated()),
         (ExplorerResourceMoveableToTrash.toNegated())
    )))
});
for (const menuId of [MenuId.EmptyEditorGroupContext, MenuId.EditorTabsBarContext]) {
    MenuRegistry.appendMenuItem(menuId, { command: { id: NEW_UNTITLED_FILE_COMMAND_ID, title: ( localizeWithPath(_moduleId, 27, "New Text File")) }, group: '1_file', order: 10 });
    MenuRegistry.appendMenuItem(menuId, { command: { id: 'workbench.action.quickOpen', title: ( localizeWithPath(_moduleId, 28, "Open File...")) }, group: '1_file', order: 20 });
}
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    group: '1_new',
    command: {
        id: NEW_UNTITLED_FILE_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 29, "&&New Text File"))
    },
    order: 1
});
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    group: '4_save',
    command: {
        id: SAVE_FILE_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 30, "&&Save")),
        precondition: ( (ContextKeyExpr.or(
            ActiveEditorContext,
             (ContextKeyExpr.and(FoldersViewVisibleContext, SidebarFocusContext))
        )))
    },
    order: 1
});
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    group: '4_save',
    command: {
        id: SAVE_FILE_AS_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 31, "Save &&As...")),
        precondition: ( (ContextKeyExpr.or(
            ActiveEditorContext,
             (ContextKeyExpr.and(FoldersViewVisibleContext, SidebarFocusContext))
        )))
    },
    order: 2
});
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    group: '4_save',
    command: {
        id: SAVE_ALL_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 32, "Save A&&ll")),
        precondition: DirtyWorkingCopiesContext
    },
    order: 3
});
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    group: '5_autosave',
    command: {
        id: ToggleAutoSaveAction.ID,
        title: ( localizeWithPath(_moduleId, 33, "A&&uto Save")),
        toggled: ( (ContextKeyExpr.notEquals('config.files.autoSave', 'off')))
    },
    order: 1
});
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    group: '6_close',
    command: {
        id: REVERT_FILE_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 34, "Re&&vert File")),
        precondition: ( (ContextKeyExpr.or(
        (ContextKeyExpr.and(ActiveEditorCanRevertContext)),
        (ContextKeyExpr.and(
             (ResourceContextKey.Scheme.notEqualsTo(Schemas.untitled)),
            FoldersViewVisibleContext,
            SidebarFocusContext
        ))))),
    },
    order: 1
});
MenuRegistry.appendMenuItem(MenuId.MenubarFileMenu, {
    group: '6_close',
    command: {
        id: CLOSE_EDITOR_COMMAND_ID,
        title: ( localizeWithPath(_moduleId, 35, "&&Close Editor")),
        precondition: ( (ContextKeyExpr.or(
            ActiveEditorContext,
             (ContextKeyExpr.and(FoldersViewVisibleContext, SidebarFocusContext))
        )))
    },
    order: 2
});
MenuRegistry.appendMenuItem(MenuId.MenubarGoMenu, {
    group: '3_global_nav',
    command: {
        id: 'workbench.action.quickOpen',
        title: ( localizeWithPath(_moduleId, 36, "Go to &&File..."))
    },
    order: 1
});

export { appendEditorTitleContextMenuItem, appendToCommandPalette };
